import React, { useCallback, useEffect, useState } from 'react';
import useKonamiSequence from '../../hooks/useKonamiSequence';

// Hidden in-app trigger for Otto's idle set, so the shots can be watched on
// REAL pages instead of the gallery.
//
// Why it exists: every shot resolves against furniture on the page it fires on
// — a card, a sidebar nav row, the home slot. /dev/otto can fire them, but it
// fires them against the GALLERY's cards, which is not what ships. The only
// way to see the snag shot tug a real brief card is to trigger it on the
// Briefs page, and until now that meant the browser console.
//
// It is deliberately invisible. Type the code word anywhere in the app (not in
// a text field) and the panel appears; Escape or Close dismisses it. No button,
// no menu item, nothing to find by accident — colleagues never meet it.
//
// ⚠️ THE CODE WORD MUST NOT START WITH "otto".
//
// The first version of this was "ottodev", which did not work: TurboParade
// already listens for "otto" and fires the seasonal parade on the fourth
// letter, before this word is finished. A sequence that merely CONTAINS an
// existing one is not a near miss — it triggers the other thing every time.
// There is a test ("code word cannot collide") that fails if a future word
// prefixes or is prefixed by a registered sequence.
//
// TO CHANGE IT: edit CODE below, and keep it clear of every sequence in
// KNOWN_SEQUENCES.
const CODE = 'shotlist';

// Every OTHER key sequence registered in the app, so the collision test has
// something to check against. Add to this when a new easter egg lands.
export const KNOWN_SEQUENCES = ['otto'];

// Nice names for the shots. Keyed by id, and MISSING IS FINE — anything not
// listed renders under its own id, so a shot added to the roamer's registry
// appears here on its own rather than silently going missing.
const LABEL = {
  peek:  'Peer over a card',
  home:  'Home-body (random idle)',
  fly:   'Fly up the sidebar',
  perch: 'Perch & slip',
  glass: 'Glass press',
  lean:  'Lean & wobble the rail',
  ledge: 'Preen on the ledge',
  nav:   'Perch down the nav',
  snag:  'Wing caught on a card',
  trio:  'Three Ottos (rare)',
};

// Shots that visibly transform live UI. Worth knowing which they are while
// reviewing: if anything stays bent after one of these, that is the bug to
// report, and it will not be visible on the ones that only overlay.
const TOUCHES_UI = new Set(['lean', 'nav', 'snag']);

export default function OttoDevPanel() {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(null);   // { id, text, ok }
  const [shots, setShots] = useState([]);
  const [motions, setMotions] = useState([]);
  // The shared listener, not a second copy of one. It already handles the
  // things a bespoke version gets wrong: ignoring input/textarea/contenteditable
  // focus, skipping while the tab is hidden, resetting on a non-matching prefix,
  // and recovering when the last key restarts the word.
  useKonamiSequence(CODE, () => {
    setShots(Array.isArray(window.__ottoShots) ? window.__ottoShots : []);
    setMotions(Array.isArray(window.__ottoAnims) ? window.__ottoAnims : []);
    setResult(null);
    setOpen(true);
  });
  useEffect(() => {
    if (!open) return undefined;
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [open]);

  // __ottoPlay returns a STRING when it can't run — "no usable target on this
  // screen", or the list of ids for a bad name. The gallery's buttons throw
  // that away, which is why a dead button there looks identical to a shot that
  // played badly. Here it is surfaced: a shot that cannot resolve says so.
  const play = useCallback((id) => {
    const fn = window.__ottoPlay;
    if (typeof fn !== 'function') {
      setResult({ id, ok: false, text: 'Otto is not on this page — the roamer only mounts for internal users.' });
      return;
    }
    const out = fn(id);
    setResult(typeof out === 'string'
      ? { id, ok: false, text: out }
      : { id, ok: true, text: 'Playing — watch the page, not this panel.' });
  }, []);

  // Body motions run through a different driver entirely (OttoAlive, on the
  // Otto you can see) rather than the roaming overlay, so they need their own
  // call. Same feedback contract: a string back means it did not run.
  const motion = useCallback((kind) => {
    const fn = window.__ottoAnim;
    if (typeof fn !== 'function') {
      setResult({ id: kind, ok: false, text: 'No interactive Otto on this page — try the dashboard or a page with the briefing bar.' });
      return;
    }
    const out = fn(kind);
    setResult(typeof out === 'string'
      ? { id: kind, ok: false, text: out }
      : { id: kind, ok: true, text: 'Playing on the Otto in the bar.' });
  }, []);

  if (!open) return null;

  return (
    // data-otto-skip so Otto can't choose the panel as a perch. Without it the
    // card-seeking shots would happily target the thing being used to trigger
    // them, which reads as a bug and is not what ships.
    <div
      data-otto-skip
      role="dialog"
      aria-label="Otto shot preview"
      className="fixed bottom-5 right-5 z-[9999] w-[290px] rounded-xl border border-[#333927] bg-[#1A1D12] p-3.5 font-['Metropolis'] text-[#EEF1E1]"
      style={{ boxShadow: '0 12px 40px -12px rgba(0,0,0,.75)' }}>

      <div className="flex items-center justify-between gap-3 mb-2.5">
        <span className="text-[10px] font-bold tracking-[0.16em] uppercase text-[#A9E52A]">Otto shots</span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-[11px] font-semibold text-[#878D77] hover:text-[#EEF1E1] bg-transparent border-0 cursor-pointer">
          Close
        </button>
      </div>

      <p className="text-[11px] leading-snug text-[#878D77] m-0 mb-2.5">
        Fires on <strong className="text-[#B4BAA4]">this</strong> page, against its real cards and nav.
      </p>

      <p className="text-[9px] font-bold tracking-[0.16em] uppercase text-[#878D77] m-0 mb-1.5">Roaming shots</p>

      <div className="flex flex-col gap-1">
        {shots.length === 0 && (
          <p className="text-[11px] text-[#878D77] m-0">No shots registered — is Otto on this page?</p>
        )}
        {shots.map(id => (
          <button
            key={id}
            type="button"
            onClick={() => play(id)}
            className="w-full text-left px-2.5 py-1.5 rounded-lg border border-[#2A2F1F] bg-[#202417] hover:border-[#A9E52A] text-[12px] text-[#EEF1E1] cursor-pointer transition-colors flex items-center justify-between gap-2">
            <span>{LABEL[id] || id}</span>
            {TOUCHES_UI.has(id) && (
              <span title="Transforms live UI — check nothing stays bent" className="text-[9px] font-bold tracking-[0.1em] uppercase text-[#E3B45C] flex-shrink-0">UI</span>
            )}
          </button>
        ))}
      </div>

      {/* The OTHER animation system. These are body motions on the Otto in the
          bar — sneeze, yawn, preen and the rest — not travelling overlay
          shots. They were unreachable from here until 2026-08-14, which is why
          "sneeze doesn't appear in my shot list" was a fair complaint. */}
      {motions.length > 0 && (
        <>
          <p className="text-[9px] font-bold tracking-[0.16em] uppercase text-[#878D77] m-0 mt-3 mb-1.5">Body motions</p>
          <div className="flex flex-wrap gap-1">
            {motions.map(kind => (
              <button
                key={kind}
                type="button"
                onClick={() => motion(kind)}
                className="px-2 py-1 rounded-md border border-[#2A2F1F] bg-[#202417] hover:border-[#A9E52A] text-[11px] text-[#EEF1E1] cursor-pointer transition-colors">
                {kind}
              </button>
            ))}
          </div>
        </>
      )}

      {result && (
        <p className={`text-[11px] leading-snug m-0 mt-2.5 pt-2.5 border-t border-[#2A2F1F] ${result.ok ? 'text-[#A9E52A]' : 'text-[#E3B45C]'}`}>
          <strong>{LABEL[result.id] || result.id}:</strong> {result.text}
        </p>
      )}
    </div>
  );
}
